import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { ApiError, radia, type ObsadenostKanala, type StavMerania } from '../api/client.js';
import { Badge, Button, Card, Notice, SectionTitle } from '../components/ui.js';

/*
 * Meranie obsadenosti pásma 2,4 GHz.
 *
 * Kanály Zigbee a Threadu boli doteraz rozumný odhad: mimo hlavných
 * Wi-Fi a desať kanálov od seba. Odhad ale nevie o mikrovlnke, o sieti
 * suseda ani o Wi-Fi na nehlavnom kanáli.
 *
 * Meria rádio Threadu, platí to ale pre obe: Zigbee aj Thread sedia
 * v tom istom pásme a používajú tie isté kanály 11 až 26.
 *
 * Výsledok sa nevyzdvihuje naraz — border router meranie rozbehne
 * a obrazovka sa naň doptáva, kým nedobehne.
 */

/** Ako často sa obrazovka doptáva na výsledok. */
const DOPYT_MS = 1500;

/**
 * Strop na čakanie.
 *
 * Meranie šestnástich kanálov trvá pod desať sekúnd. Keď do minúty
 * nedobehne, niečo sa stalo a je lepšie to povedať než krútiť kolieskom
 * donekonečna.
 */
const STROP_MS = 60_000;

/**
 * Strop na prehľadanie siete.
 *
 * Trvá dlhšie než meranie — na hardvéri okolo štyridsiatich sekúnd —
 * takže minúta by ho stihla ukončiť skôr, než dobehne.
 */
const STROP_OBJAVOVANIA_MS = 120_000;

/** Rozsah stupnice v dBm; mimo neho sa meranie v tomto pásme nedeje. */
const NAJTICHSIE_DBM = -100;
const NAJHLUCNEJSIE_DBM = -30;

/** Kde na stupnici (0 až 100) leží nameraná hodnota. */
function miera(dbm: number): number {
  const podiel = (dbm - NAJTICHSIE_DBM) / (NAJHLUCNEJSIE_DBM - NAJTICHSIE_DBM);
  return Math.max(0, Math.min(100, Math.round(podiel * 100)));
}

/**
 * Tón stĺpca podľa nameranej sily.
 *
 * Hranice sú zámerne hrubé — meranie je okamih, nie súd. Majú povedať
 * „tu je ticho / tu niečo je / tu to hučí", nie presný stupeň.
 */
function ton(dbm: number): 'ok' | 'warn' | 'danger' {
  if (dbm <= -85) return 'ok';
  if (dbm <= -70) return 'warn';
  return 'danger';
}

const FARBA: Record<'ok' | 'warn' | 'danger', string> = {
  ok: 'bg-(--color-ok)',
  warn: 'bg-(--color-warn)',
  danger: 'bg-(--color-danger)',
};

function Stlpec({
  kanal,
  zigbeeKanal,
  threadKanal,
}: {
  readonly kanal: ObsadenostKanala;
  readonly zigbeeKanal: number | null;
  readonly threadKanal: number | null;
}): ReactNode {
  const tu: string[] = [];
  if (kanal.kanal === zigbeeKanal) tu.push('Zigbee');
  if (kanal.kanal === threadKanal) tu.push('Thread');

  return (
    <li className="flex items-center gap-2 text-sm">
      <span className="w-8 shrink-0 text-right font-mono text-xs text-(--color-text-muted)">
        {kanal.kanal}
      </span>

      <span
        className="h-3 min-w-0 flex-1 overflow-hidden rounded-full bg-(--color-surface-sunken)"
        // Stupnica je obrázok hodnoty; hodnotu nesie text vedľa neho.
        aria-hidden="true"
      >
        {kanal.najhorsie !== null && (
          <span
            className={`block h-full rounded-full ${FARBA[ton(kanal.najhorsie)]}`}
            style={{ width: `${String(miera(kanal.najhorsie))}%` }}
          />
        )}
      </span>

      <span className="w-20 shrink-0 font-mono text-xs text-(--color-text)">
        {kanal.najhorsie === null ? (
          <span className="text-(--color-text-subtle)">nezmerané</span>
        ) : (
          `${String(kanal.najhorsie)} dBm`
        )}
      </span>

      <span className="w-28 shrink-0 text-xs text-(--color-text-muted)">
        {tu.length > 0 ? `teraz ${tu.join(' + ')}` : ''}
      </span>
    </li>
  );
}

function Odporucenie({ stav }: { readonly stav: StavMerania }): ReactNode {
  const o = stav.odporucenie;
  if (o === undefined) return null;

  const riadok = (
    co: string,
    k: { kanal: number; najhorsie: number; rusiaceWifi: readonly number[] } | null,
  ): ReactNode => (
    <li key={co} className="flex flex-wrap items-baseline gap-x-2">
      <span className="text-(--color-text-muted)">{co}</span>
      {k === null ? (
        <span className="text-(--color-text-subtle)">
          nie je kam, všetky zmerané sú blízko seba
        </span>
      ) : (
        <>
          <span className="font-medium text-(--color-text)">kanál {k.kanal}</span>
          <span className="font-mono text-xs text-(--color-text-muted)">
            {k.najhorsie} dBm
            {k.rusiaceWifi.length > 0 ? ` · prekrýva Wi-Fi ${k.rusiaceWifi.join(', ')}` : ''}
          </span>
        </>
      )}
    </li>
  );

  return (
    <div className="flex flex-col gap-2 rounded-lg bg-(--color-surface-sunken) p-3">
      <p className="text-sm font-medium text-(--color-text)">Podľa merania</p>

      {/*
        Keď je rozdiel medzi kanálmi v rozptyle merania, povie sa to
        PRED odporučením — inak by z toho človek čítal „prepni", hoci
        dôvod na to nie je. Najtichší kanál sa aj tak vypíše, je to fakt.
      */}
      {!o.rozdielVyznamny && (
        <p className="text-sm text-(--color-text)">
          Celé pásmo je rovnako tiché
          {o.rozptylDb !== null && ` — rozdiel medzi kanálmi je ${String(o.rozptylDb)} dB`}. To je v
          rozptyle merania, takže <strong>kanál nie je dôvod meniť</strong>. Tie, ktoré sú nastavené
          teraz, sú v poriadku.
        </p>
      )}

      <ul className="flex flex-col gap-1 text-sm">
        {riadok(o.rozdielVyznamny ? 'Zigbee' : 'Najtichší zmeraný', o.zigbee ?? null)}
        {riadok(o.rozdielVyznamny ? 'Thread' : 'Druhý dosť vzdialený', o.thread ?? null)}
      </ul>
      {o.nezmerane.length > 0 && (
        <p className="text-xs text-(--color-text-muted)">
          Nezmerané kanály: {o.nezmerane.join(', ')}. Odporučenie z časti pásma je menej isté —
          nezmeraný kanál môže byť najlepší aj najhorší.
        </p>
      )}
      <p className="text-xs text-(--color-text-muted)">
        Kanál sa týmto nemení. Prepnutie je stále na tebe nižšie v tejto obrazovke.
      </p>
      <p className="text-xs text-(--color-text-muted)">
        {/*
          Čestná výhrada: ticho v meraní znamená, že počas tých sekúnd
          nikto nevysielal. Nie je to to isté ako „pásmo je voľné".
        */}
        Meria sa tromi prechodmi po pol sekundy na kanál. Ticho teda znamená, že počas nich nikto
        nevysielal — susedova sieť, ktorá sa ozve raz za minútu, sa takto zachytiť nemusí.
      </p>
    </div>
  );
}

const POPIS_STAVU: Record<StavMerania['stav'], string> = {
  pending: 'Zaradené',
  active: 'Meria sa',
  completed: 'Hotové',
  stopped: 'Zastavené',
  failed: 'Zlyhalo',
  neznamy: 'Neznámy stav',
};

/**
 * Karta merania.
 *
 * `jeSpravca` neskrýva nič podstatné — oprávnenie si overuje server pri
 * každom volaní. Tlačidlo, ktoré by vždy skončilo zamietnutím, ale nemá
 * cenu ponúkať.
 */
export function MeranieKanalov({
  zigbeeKanal,
  threadKanal,
  jeSpravca,
  dostupne,
}: {
  readonly zigbeeKanal: number | null;
  readonly threadKanal: number | null;
  readonly jeSpravca: boolean;
  /** Beží border router? Bez neho nie je čím merať. */
  readonly dostupne: boolean;
}): ReactNode {
  const [bezi, setBezi] = useState(false);
  const [faza, setFaza] = useState<'objavovanie' | 'meranie'>('meranie');
  /** Adresa uzla, ktorý meria; nie je to brána. */
  const [uzol, setUzol] = useState<string | null>(null);
  const [stav, setStav] = useState<StavMerania | null>(null);
  const [chyba, setChyba] = useState<string | null>(null);
  const zruseny = useRef(false);

  useEffect(
    () => () => {
      zruseny.current = true;
    },
    [],
  );

  /**
   * Čaká na dokončenie úlohy. `true` znamená dobehla, `false` vzdané.
   *
   * Stav sa priebežne ukladá len pri meraní — pri prehľadávaní siete by
   * prázdny výsledok tej úlohy prepísal obrazovku na „bez hodnôt".
   */
  const pockaj = useCallback(
    async (id: string, stropMs: number, ukladaj: boolean): Promise<boolean> => {
      const do_ = Date.now() + stropMs;
      for (;;) {
        if (zruseny.current) return false;
        await new Promise((hotovo) => setTimeout(hotovo, DOPYT_MS));
        if (zruseny.current) return false;

        const vysledok = await radia.meranie(id);
        if (ukladaj) setStav(vysledok);

        if (vysledok.hotove) return true;
        if (vysledok.stav === 'failed' || vysledok.stav === 'stopped') {
          setChyba('Rádio úlohu nedokončilo.');
          return false;
        }
        if (Date.now() > do_) {
          setChyba('Úloha sa neskončila v rozumnom čase.');
          return false;
        }
      }
    },
    [],
  );

  const zmeraj = useCallback(async () => {
    setBezi(true);
    setChyba(null);
    setStav(null);
    setUzol(null);
    try {
      let zadane = await radia.spustMeranie();

      /*
       * Prvá fáza je prehľadanie siete a prichádza len vtedy, keď je
       * zbierka zariadení border routera ešte nenaplnená. Bez nej
       * nemá komu meranie zadať. Po jej skončení sa meranie vyžiada
       * znova — vtedy už zbierka naplnená je.
       */
      if (zadane.faza === 'objavovanie') {
        setFaza('objavovanie');
        if (!(await pockaj(zadane.id, STROP_OBJAVOVANIA_MS, false))) return;
        zadane = await radia.spustMeranie();
        if (zadane.faza === 'objavovanie') {
          setChyba('Sieť sa prehľadať nepodarilo; v zbierke zariadení nič nie je.');
          return;
        }
      }

      setFaza('meranie');
      setUzol(zadane.uzol ?? null);
      await pockaj(zadane.id, STROP_MS, true);
    } catch (e) {
      /*
       * Správu dodáva server — on jediný vie, prečo to nešlo: či rádio
       * práve meria, alebo border router tú úlohu vôbec nepozná. „Skús
       * to znova" pri druhom prípade by bolo zavádzajúce.
       */
      setChyba(
        e instanceof ApiError && e.message.length > 0
          ? e.message
          : 'Meranie sa nepodarilo. Skús to znova za chvíľu.',
      );
    } finally {
      if (!zruseny.current) setBezi(false);
    }
  }, [pockaj]);

  /*
    Zmeralo sa aspoň niečo? Nezmeraný kanál nie je ticho — a zoznam so
    samými nezmeranými kanálmi nie je výsledok.
  */
  const zmeraneNieco = (stav?.obsadenost ?? []).some((k) => k.najhorsie !== null);

  return (
    <Card className="flex min-w-0 flex-col gap-3 p-4 sm:p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SectionTitle>Obsadenosť pásma</SectionTitle>
        {stav !== null && (
          <Badge tone={stav.hotove && zmeraneNieco ? 'ok' : 'neutral'}>
            {stav.hotove && !zmeraneNieco ? 'Bez hodnôt' : POPIS_STAVU[stav.stav]}
          </Badge>
        )}
      </div>

      <p className="text-sm text-(--color-text-muted)">
        Rádio Threadu zmeria silu signálu na každom kanáli. Platí to pre obe siete — Zigbee aj
        Thread používajú tie isté kanály 11 až 26, takže jedno meranie poradí obom. Meranie si na
        niekoľko sekúnd vezme rolu commissionera.
      </p>

      {!dostupne && (
        <Notice tone="warn">
          <p>Border router neodpovedá, takže merať nie je čím.</p>
        </Notice>
      )}

      {jeSpravca && dostupne && (
        <div>
          <Button
            onClick={() => {
              void zmeraj();
            }}
            disabled={bezi}
          >
            {bezi
              ? faza === 'objavovanie'
                ? 'Prehľadávam sieť…'
                : 'Meriam…'
              : 'Zmerať obsadenosť'}
          </Button>
        </div>
      )}

      {chyba !== null && (
        <Notice tone="danger">
          <p>{chyba}</p>
        </Notice>
      )}

      {uzol !== null && (
        <p className="text-xs text-(--color-text-muted)">
          {/*
            Meria INÉ zariadenie, nie brána — brána si žiadosť o meranie
            sama sebe nevyhoví. Preto sa hovorí, čie meranie to je:
            rušenie je tam, kde stojí to zariadenie.
          */}
          Meralo zariadenie <span className="font-mono">{uzol}</span>, nie brána. Hodnoty platia pre
          jeho miesto.
        </p>
      )}

      {stav?.obsadenost !== undefined && stav.obsadenost.length > 0 && (
        <ul className="flex flex-col gap-1">
          {stav.obsadenost.map((k) => (
            <Stlpec key={k.kanal} kanal={k} zigbeeKanal={zigbeeKanal} threadKanal={threadKanal} />
          ))}
        </ul>
      )}

      {/*
        Odporúčanie sa ukáže LEN vtedy, keď sa naozaj niečo zmeralo.

        Border router vracia zápis pre každý kanál v maske aj vtedy, keď
        neprišla ani jedna vzorka — zoznam teda nie je prázdny, len sú
        v ňom samé nezmerané kanály. Prvá podoba tejto obrazovky preto
        varovanie neukázala a namiesto neho vypísala odporúčanie
        s dôvodom „všetky zmerané sú blízko seba", hoci zmerané nebolo
        nič (2026-10-16).
      */}
      {zmeraneNieco && stav !== null && <Odporucenie stav={stav} />}

      {stav !== null && stav.hotove && !zmeraneNieco && (
        <Notice tone="warn">
          <p className="font-medium">Meranie dobehlo, ale rádio nevrátilo ani jednu hodnotu.</p>
          <p className="mt-1">
            Stav „hotové" to ešte neznamená: border router označí úlohu za dokončenú, keď vyprší jej
            časovač, aj keď odpoveď nedošla.
          </p>
        </Notice>
      )}
    </Card>
  );
}
